import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { ApiError, api } from "../lib/api";
import type { Admin } from "../lib/types";

interface AuthState {
  admin: Admin | null;
  /** Null while the initial /me probe is still in flight. */
  ready: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [ready, setReady] = useState(false);

  // The session lives in an httpOnly cookie the page can't read, so the only
  // way to know whether we're signed in is to ask.
  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((me) => !cancelled && setAdmin(me))
      .catch((err) => {
        if (!(err instanceof ApiError && err.status === 401)) console.error(err);
      })
      .finally(() => !cancelled && setReady(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    setAdmin(await api.login(username, password));
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setAdmin(null);
  }, []);

  return (
    <AuthContext.Provider value={{ admin, ready, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
